<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <HeadlessUIDialog as="div" class="relative z-30">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="w-full lg:ml-20 max-w-3xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all relative"
            >
              <div class="absolute top-4 right-4">
                <XIcon class="w-6 h-6 cursor-pointer" @click="closeSaleModal" />
              </div>

              <InsuranceSaleTitle :product="product" :contractType="contractType" />

              <div v-if="!contractsAvailable" class="flex">
                <div class="text-sm font-medium text-red-700 pt-2">
                  <p class="break-words text-center">
                    {{ $t("There are no contracts available for this price") }}
                  </p>
                </div>
              </div>

              <SuccessModal
                v-else-if="saleCreationSuccess"
                :customer="customer"
                :matchedContractId="contractSelected?.id"
              />

              <div v-else>
                <div class="my-2">
                  <Alert type="info" class="mb-0">
                    <template #icon>💡</template>
                    <p class="font-medium text-sm">Ce contrat est encaissé directement par votre magasin.</p>
                  </Alert>
                </div>
                <VeeForm v-slot="{ handleSubmit }" as="div">
                  <form class="space-y-4" @submit="handleSubmit($event, handleOrderCreation)" ref="saleCreationForm">
                    <div class="flex flex-col">
                      <div class="flex gap-4 flex-col sm:flex-row">
                        <RadioGroup v-model="contractSelected">
                          <RadioGroupLabel class="font-bold text-xl pb-2 tracking-tight text-gray-900">
                            Durée du contrat
                          </RadioGroupLabel>

                          <div class="flex gap-4 mt-4 flex-col sm:flex-row">
                            <RadioGroupOption
                              as="template"
                              v-for="contract in yearlyContractsSortedByTermLength"
                              :key="contract.sku"
                              :value="contract"
                              v-slot="{ checked, active }"
                            >
                              <div
                                :class="[
                                  checked ? 'border-transparent' : 'border-gray-300',
                                  active && contractSelected ? 'border-primary ring-2 ring-primary' : '',
                                  'relative flex border bg-white p-4 shadow-xs focus:outline-hidden grow rounded-3xl cursor-pointer',
                                ]"
                              >
                                <span class="flex flex-1">
                                  <span class="flex flex-col">
                                    <RadioGroupLabel as="span" class="block text-md font-medium text-gray-900">
                                      {{ contractDisplayTermLength(contract) }}
                                    </RadioGroupLabel>
                                    <RadioGroupDescription
                                      as="span"
                                      class="mt-1 flex items-center text-md text-gray-500"
                                    >
                                      {{ formatPrice(contract.price) }}
                                    </RadioGroupDescription>
                                    <RadioGroupDescription
                                      as="span"
                                      class="mt-1 flex items-center text-xs text-gray-500"
                                    >
                                      {{ contract.sku }}
                                    </RadioGroupDescription>
                                  </span>
                                </span>
                                <CheckCircleIcon
                                  :class="[!checked ? 'invisible' : '', 'h-5 w-5 text-primary-dark']"
                                  aria-hidden="true"
                                />
                                <span
                                  :class="[
                                    active && contractSelected ? 'border' : 'border-2',
                                    checked ? 'border-primary' : 'border-transparent',
                                    'pointer-events-none absolute -inset-px rounded-3xl cursor-pointer',
                                  ]"
                                  aria-hidden="true"
                                />
                              </div>
                            </RadioGroupOption>
                          </div>
                          <p class="mt-2 text-sm text-red-600" v-show="showContractRequiredErrorMessage">
                            {{ $t("Please select the contract sold.") }}
                          </p>
                        </RadioGroup>
                      </div>
                    </div>

                    <div class="mt-6">
                      <div class="flex flex-row gap-4">
                        <div class="sm:w-1/2">
                          <button
                            class="w-full border border-transparent text-button-font-primary bg-primary px-4 py-2 text-lg font-medium transition duration-300 transform hover:scale-105 rounded-lg focus:outline-hidden"
                            type="submit"
                          >
                            {{ $t("Record a sale") }}
                          </button>
                        </div>

                        <div class="sm:w-1/2">
                          <button
                            class="w-full border border-transparent bg-red-100 px-4 py-2 text-lg font-medium text-red-700 transition duration-300 transform hover:scale-105 rounded-lg focus:outline-hidden"
                            @click="closeSaleModal"
                          >
                            {{ $t("Close") }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </VeeForm>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </HeadlessUIDialog>
  </TransitionRoot>
</template>

<script>
import { Form as VeeForm } from "vee-validate";
import {
  Dialog as HeadlessUIDialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
  RadioGroup,
  RadioGroupDescription,
  RadioGroupLabel,
  RadioGroupOption,
} from "@headlessui/vue";
import { CheckCircleIcon, XIcon } from "@heroicons/vue/outline";
import InsuranceSaleTitle from "@/components/payment/InsuranceSaleTitle.vue";
import SuccessModal from "@/components/pos/subscription/utils/SuccessModal.vue";
import { Alert, formatPrice, formatDuration } from "@estaly/ui";
import OrdersService from "@/api/services/orders";
import { mapActions, mapState } from "vuex";

export default {
  components: {
    VeeForm,
    HeadlessUIDialog,
    DialogPanel,
    TransitionChild,
    TransitionRoot,
    RadioGroup,
    RadioGroupDescription,
    RadioGroupLabel,
    RadioGroupOption,
    CheckCircleIcon,
    XIcon,
    InsuranceSaleTitle,
    SuccessModal,
    Alert,
  },

  props: {
    isOpen: Boolean,
    saleRecording: Object,
    customer: Object,
    address: Object,
    sellerId: String,
    product: Object,
    category: String,
    contractType: String,
  },

  data: () => {
    return {
      subscriptionLink: "",
      contractSelected: "",
      saleCreationSuccess: false,
      showContractRequiredErrorMessage: false,
    };
  },

  computed: {
    ...mapState("store", ["selectedStoreId"]),

    contractsAvailable() {
      return this.saleRecording.contractDetails.length > 0;
    },

    yearlyContractsSortedByTermLength() {
      const contracts = this.saleRecording.contractDetails;
      const filteredContracts = contracts.filter((contract) => !contract.isMonthly);

      return filteredContracts.sort((a, b) => a.termLength - b.termLength);
    },

    orderParams() {
      const customerParams = {
        email: this.customer.email,
        first_name: this.customer.firstName,
        last_name: this.customer.lastName,
        phone: this.customer.phone,
        birth_date: this.customer.birthDate,
        professional_customer: this.customer.professionalCustomer,
        legal_form: this.customer.legalForm,
        company_name: this.customer.companyName,
        buyer_role: this.customer.buyerRole,
        siren_number: this.customer.sirenNumber,
      };
      const productParams = {
        title: this.product.title,
        price: this.product.price,
        reference_id: this.product.referenceId,
      };
      const insuranceParams = {
        offer_id: this.contractSelected.sku,
        price: this.contractSelected.price,
        quantity: 1,
        product: productParams,
        product_category: this.category,
      };
      const addressParams = {
        address1: this.address.street,
        city: this.address.city,
        province: this.address.province,
        country: this.address.country,
        zip_code: this.address.zipCode,
      };

      return {
        source: "offline",
        order: {
          customer: customerParams,
          insurance: insuranceParams,
          address: addressParams,
          seller_id: this.sellerId,
          charged_by: "merchant",
        },
      };
    },
  },

  watch: {
    contractSelected: {
      handler(newValue) {
        if (newValue) this.showContractRequiredErrorMessage = false;
      },
    },
  },

  methods: {
    ...mapActions("store", ["withLoader"]),
    ...mapActions("notifications", ["notify"]),
    formatPrice,

    resetAttributes() {
      this.subscriptionLink = "";
      this.contractSelected = "";
      this.saleCreationSuccess = false;
      this.showContractRequiredErrorMessage = false;
    },

    contractDisplayTermLength(contract) {
      return formatDuration(contract.termLength);
    },

    closeSaleModal() {
      this.$emit("closeSaleModal", this.saleCreationSuccess);
      if (this.saleCreationSuccess) {
        this.resetAttributes();
      }
    },

    async handleOrderCreation() {
      if (!this.contractSelected) {
        this.showContractRequiredErrorMessage = true;
        this.notify({
          category: "simple",
          type: "error",
          text: "Veuillez sélectionner un contrat avant de continuer.",
        });
        return;
      }

      await this.withLoader(async () => {
        const response = await OrdersService.create(this.selectedStoreId, this.orderParams);
        if (response?.success) {
          this.saleCreationSuccess = true;
          this.showContractRequiredErrorMessage = false;
        }
      });
    },
  },
};
</script>
